import { Injectable } from '@angular/core';
import { ValueType, ValueTypes } from '@app/core/constants/global-enum';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LiveDTO } from '../dtos/live';

@Injectable({
    providedIn: 'root'
})
export class UtilityService {

    constructor() { }


    public resolveIcon(iconData: any, fallback: string = '["fal","question"]'): IconProp {
        // Accepts:
        // - IconProp tuple: ['fal','circle-parking']
        // - IconLookup/IconDefinition objects
        // - JSON stringified IconProp: '["fal","circle-parking"]'
        // - Flat format: 'fal:circle-parking'
        // - Plain iconName string: 'circle-parking' (defaults to 'fal')

        const fallbackIcon = (): IconProp => {
            try {
                return JSON.parse(String(fallback).replace(/'/g, '"'));
            } catch {
                return ['fal', 'question'] as IconProp;
            }
        };

        if (iconData === null || iconData === undefined) {
            return fallbackIcon();
        }

        // Already a tuple: ['fal','circle-parking']
        if (Array.isArray(iconData) && iconData.length >= 2) {
            const [prefix, iconName] = iconData;
            if (typeof prefix === 'string' && typeof iconName === 'string') {
                return [prefix, iconName] as IconProp;
            }
            return iconData as IconProp;
        }

        // IconLookup/IconDefinition objects
        if (typeof iconData === 'object') {
            // Some callers pass IconLookup-like objects; normalize minimal shape.
            const maybePrefix = (iconData as any)?.prefix;
            const maybeIconName = (iconData as any)?.iconName;
            if (typeof maybePrefix === 'string' && typeof maybeIconName === 'string') {
                return { prefix: maybePrefix, iconName: maybeIconName } as IconProp;
            }
            return iconData as IconProp;
        }

        const raw = String(iconData).trim();
        if (!raw) {
            return fallbackIcon();
        }

        // Flat format: "fal:circle-parking"
        if (!raw.startsWith('[') && !raw.startsWith('{') && raw.includes(':')) {
            const [prefix, iconName] = raw.split(':', 2).map((v) => v.trim());
            if (prefix && iconName) {
                return [prefix, iconName] as IconProp;
            }
        }

        // JSON stringified IconProp / IconLookup
        const iconConfig = raw.replace(/'/g, '"');
        if ((iconConfig.startsWith('[') && iconConfig.endsWith(']')) || (iconConfig.startsWith('{') && iconConfig.endsWith('}'))) {
            try {
                const parsed = JSON.parse(iconConfig);
                // Re-run through the normalizer to cover nested cases.
                return this.resolveIcon(parsed, fallback);
            } catch {
                // Fall through to plain iconName handling.
            }
        }

        // Plain iconName string (default to 'fal')
        return ['fal', raw] as IconProp;
    }

    public flatIcon(iconData: any): string | undefined {
        const ip = this.resolveIcon(iconData);
        if (ip) {
            return `${ip[0]}:${ip[1]}`;
        } else {
            return undefined;
        }
    }

    public resolveValue<T>(val: any, valueType: ValueTypes): T {
        switch (+valueType) {
            case ValueTypes.Number:
            case ValueTypes.Currency:
                return (+val) as unknown as T; // cast number
            case ValueTypes.Boolean:
                return (val.toLowerCase() === 'true') as unknown as T; //cast boolean
            case ValueTypes.DateTime:
                return new Date(val) as unknown as T; //cast date
            case ValueTypes.String:
            case ValueTypes.Password:
            case ValueTypes.Color:
            case ValueTypes.PhotoUrl:
            case ValueTypes.Link:
            default:
                return val as unknown as T; // vat ken ay du sam tayms

        }
    }

}
